import { aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';

/**
 * Columna por la que se descuelga cada tabla del cliente al que pertenece.
 *
 * Se enumeran a mano porque no todas las tablas guardan el cliente: unas lo
 * tienen directo, otras sólo el área, y unas pocas —como `clientes`— son el
 * cliente. Adivinarlo por el nombre daría filtros silenciosamente incompletos,
 * que es peor que no filtrar.
 */
const POR_CLIENTE = [
  'areas',
  'vw_contactos_alerta_detalle',
  'v2_politicas_escalamiento',
  'v3_vw_actuadores_historial_ui',
  'perfiles',
  'perfiles_clientes_acceso',
  'route_plans',
  'gps_pois',
  'cctv_grabadores',
  'smart_meter_config',
  'smart_meter_thresholds',
  'contactos_alerta',
  'politicas_escalamiento',
  'integraciones_webhooks',
  'monthly_report_configs',
  'user_locations',
  'user_locations_latest',
  'vw_alarmas_frontend',
  'vw_alarmas_ekm_frontend',
  'vw_outbox_operativo',
  'vw_ops_notif_fail_ui',
  'vw_sensores_detallado',
  'vw_nodos_info',
  'vw_monthly_report_configs_v2',
  'vw_monthly_report_runs_detalle',
  'v3_vw_actuadores_control_ui',
  'v2_vw_tipos_actuadores_cliente_ui',
] as const;

/** Tablas que sólo saben de qué área son; el cliente se deduce del área. */
const POR_AREA = ['nodos'] as const;

/**
 * Vistas que traen el nombre del cliente pero no su identificador.
 *
 * Las de umbrales se arman desde el sensor y arrastran el cliente sólo como
 * texto. Comparar por nombre es más frágil que por identificador —dos clientes
 * homónimos se confundirían—, pero es lo único que ofrecen, y sin esto los
 * umbrales del cliente de muestra se colaban en la lista.
 */
const POR_NOMBRE_CLIENTE = [
  'vw_umbrales_analogicos_completos',
  'vw_umbrales_digitales_completos',
] as const;

/** Los tres métodos del constructor de consultas que hacen falta aquí. */
interface Excluible {
  neq(columna: string, valor: unknown): unknown;
  not(columna: string, operador: string, valor: unknown): unknown;
}

/**
 * Oculta del sistema el cliente de demostración.
 *
 * Existe para que quien administra la plataforma pueda mirar los datos reales
 * sin que se le mezclen los de la cuenta de muestra. No es un permiso: los
 * datos siguen siendo accesibles, sólo se apartan de la vista de quien lo pide.
 *
 * El identificador del cliente se busca por su nombre en vez de dejarlo fijo
 * en el código: si algún día se borra y se vuelve a crear, esto sigue
 * funcionando sin tocar nada.
 */
export class FiltroDemo {
  private clienteId: number | null = null;
  private clienteNombre: string | null = null;
  private areaIds: ReadonlySet<number> = new Set();
  private activo = false;
  private preparando: Promise<void> | null = null;

  /** `true` cuando hay algo que ocultar y el usuario lo ha pedido. */
  get aplica(): boolean {
    return this.activo && this.clienteId !== null;
  }

  get identificado(): boolean {
    return this.clienteId !== null;
  }

  establecerActivo(activo: boolean): void {
    this.activo = activo;
  }

  /**
   * Averigua cuál es el cliente de demostración y de qué áreas es dueño.
   *
   * Se hace una sola vez por sesión; las llamadas siguientes reutilizan la
   * misma promesa para no repetir la consulta.
   */
  preparar(): Promise<void> {
    if (this.preparando) return this.preparando;

    this.preparando = (async () => {
      const { data } = await supabase
        .from('clientes')
        .select('id, nombre')
        .ilike('nombre', '%demo%')
        .order('id', { ascending: true });

      const candidatos = ((data ?? []) as Record<string, unknown>[]).map((fila) => ({
        id: aNumero(fila.id),
        nombre: (aTexto(fila.nombre) ?? '').trim(),
      }));

      // Se prefiere el que se llama exactamente «Demo»; si no, el que empieza
      // por «demo»; y en último caso cualquiera que lo contenga.
      const elegido =
        candidatos.find((c) => /^demo$/i.test(c.nombre)) ??
        candidatos.find((c) => /^demo/i.test(c.nombre)) ??
        candidatos[0];

      this.clienteId = elegido?.id ?? null;
      this.clienteNombre = elegido?.nombre ?? null;
      if (this.clienteId === null) return;

      const { data: areas } = await supabase
        .from('areas')
        .select('id')
        .eq('cliente_id', this.clienteId);

      const ids = new Set<number>();
      for (const fila of (areas ?? []) as Record<string, unknown>[]) {
        const id = aNumero(fila.id);
        if (id !== null) ids.add(id);
      }
      this.areaIds = ids;
    })();

    return this.preparando;
  }

  /** Olvida lo aprendido; al cambiar de sesión el cliente puede ser otro. */
  reiniciar(): void {
    this.clienteId = null;
    this.clienteNombre = null;
    this.areaIds = new Set();
    this.preparando = null;
  }

  /** ¿Este cliente es el de demostración? */
  esDemo(clienteId: number | null | undefined): boolean {
    return this.aplica && clienteId != null && clienteId === this.clienteId;
  }

  /**
   * Añade a la consulta la exclusión que corresponda a esa tabla.
   *
   * Devuelve la consulta tal cual cuando el filtro no aplica o la tabla no
   * tiene forma de saber de qué cliente es: en ese caso es mejor mostrar de
   * más que ocultar datos reales por error.
   */
  aplicar<T>(tabla: string, consulta: T): T {
    if (!this.aplica || this.clienteId === null) return consulta;

    const constructor = consulta as Excluible;

    if (tabla === 'clientes') return constructor.neq('id', this.clienteId) as T;

    if ((POR_CLIENTE as readonly string[]).includes(tabla)) {
      return constructor.neq('cliente_id', this.clienteId) as T;
    }

    if ((POR_AREA as readonly string[]).includes(tabla) && this.areaIds.size > 0) {
      return constructor.not('area_id', 'in', `(${[...this.areaIds].join(',')})`) as T;
    }

    if ((POR_NOMBRE_CLIENTE as readonly string[]).includes(tabla) && this.clienteNombre) {
      return constructor.neq('cliente_nombre', this.clienteNombre) as T;
    }

    return consulta;
  }
}

export const filtroDemo = new FiltroDemo();
